import { Box, CircularProgress, useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

import { CbhIcon } from "../CbhIcon";
import { Text } from "../Text";

const THUMB_SIZE = 48;
const TRACK_PADDING = 8;
const TRACK_HEIGHT = THUMB_SIZE + TRACK_PADDING * 2;
const COMPLETION_THRESHOLD = 0.8;

export interface SlideToConfirmButtonProps {
  ctaText: string;
  /**
   * Text displayed after a successful slide, replacing the CTA text.
   */
  successText?: string;
  isLoading?: boolean;
  /**
   * When true, the button shows `successText` and removes its border/background
   * so it no longer looks like a button. Waits for the minimum loading duration
   * (`minIsLoadingMs`) before taking effect.
   */
  success?: boolean;
  /**
   * Called when the thumb is slid past the completion threshold (80%).
   */
  onConfirm: () => void;
  /**
   * Called after the success state has been displayed for `onSuccessShownDelayMs`.
   * Useful for resetting the component or navigating away.
   */
  onSuccessShown?: () => void;
  /**
   * How long to display the success state before calling `onSuccessShown`.
   * @default 1500
   */
  onSuccessShownDelayMs?: number;
  /**
   * Minimum duration the loading state is shown, even if `isLoading` becomes
   * false sooner. Prevents flickering for fast responses.
   * @default 1000
   */
  minIsLoadingMs?: number;
  /**
   * When true, uses destructive (error) colors for the fill and icon.
   */
  destructive?: boolean;
  disabled?: boolean;
}

interface DragState {
  startX: number;
  trackWidth: number;
}

// eslint-disable-next-line complexity
export function SlideToConfirmButton(props: SlideToConfirmButtonProps) {
  const {
    ctaText,
    successText,
    isLoading = false,
    success = false,
    onConfirm,
    onSuccessShown,
    onSuccessShownDelayMs = 1500,
    minIsLoadingMs = 1000,
    destructive = false,
    disabled = false,
  } = props;
  const theme = useTheme();

  const trackRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<DragState | undefined>(undefined);
  const [offsetX, setOffsetX] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoadingLocked, setIsLoadingLocked] = useState(false);
  const loadingStartRef = useRef(0);
  // Stored in a ref so that changing the callback identity doesn't restart the
  // success-shown timer — callers rarely memoize this prop.
  const onSuccessShownRef = useRef(onSuccessShown);
  onSuccessShownRef.current = onSuccessShown;

  useEffect(() => {
    if (isLoading) {
      loadingStartRef.current = Date.now();
      setIsLoadingLocked(true);
      return;
    }

    if (!isLoadingLocked) {
      return;
    }

    const elapsed = Date.now() - loadingStartRef.current;
    const remaining = Math.max(0, minIsLoadingMs - elapsed);

    const timerId = setTimeout(() => {
      setIsLoadingLocked(false);
    }, remaining);

    return () => {
      clearTimeout(timerId);
    };
  }, [isLoading, isLoadingLocked, minIsLoadingMs]);

  const effectiveIsLoading = isLoading || isLoadingLocked;
  const effectiveSuccess = success && !effectiveIsLoading;

  const fillColor = destructive ? theme.palette.error.main : theme.palette.primary.main;
  const contrastColor = destructive
    ? theme.palette.error.contrastText
    : theme.palette.primary.contrastText;
  const textColor = destructive
    ? (theme.palette.intent?.error.text ?? theme.palette.error.main)
    : theme.palette.text.primary;
  const prevActiveRef = useRef(effectiveIsLoading || effectiveSuccess);

  useEffect(() => {
    const wasActive = prevActiveRef.current;
    const isActive = effectiveIsLoading || effectiveSuccess;

    if (wasActive && !isActive) {
      setIsTransitioning(true);
      setOffsetX(0);
      setIsComplete(false);
    }

    prevActiveRef.current = isActive;
  }, [effectiveIsLoading, effectiveSuccess]);

  useEffect(() => {
    if (!effectiveSuccess || !onSuccessShownRef.current) {
      return;
    }

    const timerId = setTimeout(() => {
      onSuccessShownRef.current?.();
    }, onSuccessShownDelayMs);

    return () => {
      clearTimeout(timerId);
    };
  }, [effectiveSuccess, onSuccessShownDelayMs]);

  const getMaxOffset = useCallback(() => {
    if (!trackRef.current) {
      return 0;
    }

    return trackRef.current.clientWidth - THUMB_SIZE - TRACK_PADDING * 2;
  }, []);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (disabled || effectiveIsLoading || isComplete) {
        return;
      }

      const track = trackRef.current;
      if (!track) {
        return;
      }

      event.currentTarget.setPointerCapture(event.pointerId);

      dragStateRef.current = {
        startX: event.clientX,
        trackWidth: track.clientWidth,
      };
      setIsTransitioning(false);
    },
    [disabled, effectiveIsLoading, isComplete]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        return;
      }

      const currentMax = getMaxOffset();
      const delta = event.clientX - dragState.startX;
      setOffsetX(Math.max(0, Math.min(delta, currentMax)));
    },
    [getMaxOffset]
  );

  const handlePointerUp = useCallback(() => {
    const dragState = dragStateRef.current;
    if (!dragState) {
      return;
    }

    dragStateRef.current = undefined;
    const currentMax = getMaxOffset();

    if (currentMax > 0 && offsetX / currentMax >= COMPLETION_THRESHOLD) {
      setOffsetX(currentMax);
      setIsTransitioning(true);
      setIsComplete(true);
      onConfirm();
    } else {
      setIsTransitioning(true);
      setOffsetX(0);
    }
  }, [getMaxOffset, offsetX, onConfirm]);

  const isFilled = effectiveIsLoading || effectiveSuccess;
  const fillWidth = isFilled ? "100%" : offsetX + THUMB_SIZE + TRACK_PADDING * 2;

  const isInteractive = !disabled && !effectiveIsLoading && !effectiveSuccess && !isComplete;
  const showThumb = !effectiveIsLoading && !effectiveSuccess;

  return (
    <Box
      ref={trackRef}
      sx={{
        position: "relative",
        height: TRACK_HEIGHT,
        borderRadius: theme.borderRadius?.xLarge,
        border: "none",
        boxShadow: effectiveSuccess
          ? "none"
          : `inset 0 0 0 ${theme.borderWidth?.regular} ${theme.palette.border?.primary}`,
        backgroundColor: effectiveSuccess ? "transparent" : theme.palette.background.tertiary,
        overflow: "hidden",
        userSelect: "none",
        touchAction: "none",
        opacity: disabled ? 0.5 : 1,
        cursor: isInteractive ? "grab" : "default",
      }}
    >
      {/* Colored fill that extends with the thumb — sits above text to obscure it */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: fillWidth,
          backgroundColor: fillColor,
          opacity: effectiveSuccess ? 0 : 1,
          transition: [
            isTransitioning || isFilled ? "width 300ms ease-out" : undefined,
            "opacity 400ms ease-out",
          ]
            .filter(Boolean)
            .join(", "),
          borderRadius: theme.borderRadius?.xLarge,
          zIndex: 1,
        }}
      />

      {/* CTA text — behind the fill so it gets obscured */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          // ensure text doesn't get cut off by the thumb
          paddingX: `${THUMB_SIZE + TRACK_PADDING * 2 + 4}px`,
          zIndex: 0,
        }}
      >
        {!effectiveSuccess && (
          <Text semibold variant="body1" sx={{ color: textColor, textAlign: "center" }}>
            {ctaText}
          </Text>
        )}
      </Box>

      {/* Loading spinner — centered in the full track, above the fill */}
      {effectiveIsLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3,
          }}
        >
          <CircularProgress size={24} sx={{ color: contrastColor }} />
        </Box>
      )}

      {/* Success text — centered above the fill */}
      {effectiveSuccess && successText && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 3,
          }}
        >
          <Text semibold variant="body1" sx={{ color: textColor }}>
            {successText}
          </Text>
        </Box>
      )}

      {/* Draggable thumb */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          left: TRACK_PADDING + offsetX,
          width: THUMB_SIZE,
          height: THUMB_SIZE,
          borderRadius: "50%",
          backgroundColor: theme.palette.background.tertiary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2,
          transition: [
            isTransitioning ? "left 300ms ease-out" : undefined,
            "opacity 300ms ease-out",
          ]
            .filter(Boolean)
            .join(", "),
          opacity: showThumb ? 1 : 0,
          pointerEvents: showThumb ? "auto" : "none",
          cursor: isInteractive ? "grab" : "default",
          "&:active": {
            cursor: isInteractive ? "grabbing" : "default",
          },
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <CbhIcon type="chevron-right" sx={{ color: fillColor, fontSize: 20 }} />
      </Box>
    </Box>
  );
}
